/**
 * Subscription Service for Frontend
 * 
 * Handles subscription management operations like cancellation
 */

import { AuthCore } from '../core/AuthCore';

interface CancelSubscriptionRequest {
  reason?: string;
}

interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  membershipExpiresAt?: string;
  note?: string;
}

class SubscriptionService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
  }

  /**
   * Get authentication headers
   */
  private async getAuthHeader(): Promise<Record<string, string>> {
    const authCore = AuthCore.getInstance();
    const token = await authCore.getAccessToken();
    
    if (!token) {
      throw new Error('Authentication required');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Cancel user's active subscription
   */
  async cancelSubscription(request: CancelSubscriptionRequest = {}): Promise<CancelSubscriptionResponse> {
    try {
      const headers = await this.getAuthHeader();
      
      const response = await fetch(`${this.baseUrl}/api/paypal/cancel-subscription`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Subscription cancellation failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService();
